// ============================================================
// THE FLOOR'S GRID - which lines, and how it fades round the action (shared by every map renderer).
//
// A grid on the ground at the map's own tile size, capped so that zooming out of a large arena does not emit
// thousands of hairlines nobody can see. The cap is a rendering budget, not a rule: the tiles themselves are
// unaffected. Lines run the width of the arena's extent; the svg projects and cuts them at the eye
// (battlefieldScene/sceneDrawing `gridLines`), the GPU map draws them as they are (perf overhaul 2b).
//
// Pure. Zero React dependency.
// ============================================================
import { boundaryExtent, tileOf } from './boundary';
import type { Boundary } from './boundary';
import type { Camera, Viewport } from './projection';
import { project, screenRight } from './projection';
import type { Vec3 } from './types';

const MAX_GRID_LINES = 60;

export interface GridSegment {
  /** Stable per line: `x<metres>` or `z<metres>`. */
  key: string;
  a: Vec3;
  b: Vec3;
}

/** The grid's lines on the ground, coarsened to whole multiples of the tile so they stay on tile edges. */
export function gridSegments(boundary: Boundary): GridSegment[] {
  const { min, max } = boundaryExtent(boundary);
  const span = Math.max(max.x - min.x, max.z - min.z);
  const tile = tileOf(boundary);
  const step = tile * Math.max(1, Math.ceil(span / tile / MAX_GRID_LINES));
  const out: GridSegment[] = [];
  for (let x = Math.ceil(min.x / step) * step; x <= max.x; x += step)
    out.push({ key: `x${x}`, a: { x, y: 0, z: min.z }, b: { x, y: 0, z: max.z } });
  for (let z = Math.ceil(min.z / step) * step; z <= max.z; z += step)
    out.push({ key: `z${z}`, a: { x: min.x, y: 0, z }, b: { x: max.x, y: 0, z } });
  return out;
}

/** FINE NEAR, COARSE FAR (plan map-display, C): the grid is gone this far from the action. */
export const GRID_FADE_M = 22;

/**
 * The grid's fade around the action, in the projection's own space (`view`): a circle centred on the action's
 * screen point, as wide as GRID_FADE_M along the screen's right. The svg draws it as a radial mask; the GPU map
 * scales it into its canvas (gpu/floorBuffers `fadeCircle`).
 */
export function gridFadeCircle(at: Vec3, camera: Camera, view: Viewport): { x: number; y: number; r: number } {
  const c = project(at, camera, view);
  const right = screenRight(camera.yaw);
  const e = project({ x: at.x + right.x * GRID_FADE_M, y: at.y, z: at.z + right.z * GRID_FADE_M }, camera, view);
  return { x: c.x, y: c.y, r: Math.hypot(e.x - c.x, e.y - c.y) };
}
